import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  asTeammateId,
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { inputStyle } from "@repo/ui/Inputs/input";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { selectStyle } from "@repo/ui/Select/style";
import { IconSelector } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Collection,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxLoadMoreItem,
  Popover,
  SearchField,
  Select,
  SelectValue,
} from "react-aria-components";
import {
  useBasicTeammateQuery,
  useTeammatesPaginatedInfiniteQuery,
} from "../../../api/entities/teammate.js";

interface TeammateManagerCellProps {
  managerId: TeammateId | null | undefined;
  managerDisplayName: string | undefined;
  isDisabled: boolean;
  onChange: (managerId: TeammateId | null) => void;
}

interface ManagerOption {
  readonly id: string;
  readonly name: string;
}

const NONE_OPTION_ID = "_none";

export const TeammateManagerCell: FC<TeammateManagerCellProps> = (props) => {
  const { data: manager } = useBasicTeammateQuery(
    props.managerId && !props.managerDisplayName ? props.managerId : skipToken,
  );

  const fetchedManagerDisplayName = useMemo(
    () =>
      manager
        ? buildTeammateDisplayNames(manager).primaryDisplayName
        : undefined,
    [manager],
  );

  const managerDisplayName = props.managerId
    ? (props.managerDisplayName ?? fetchedManagerDisplayName ?? "Unknown")
    : undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState(search);

  useEffect(() => {
    if (!search) {
      setSearchDebounced("");
      return;
    }

    const timer = setTimeout(() => {
      setSearchDebounced(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: teammatesData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTeammatesPaginatedInfiniteQuery(
    isOpen ? { search: searchDebounced } : skipToken,
  );

  const teammates = useMemo(
    () => teammatesData?.pages.flatMap((p) => p.teammates) ?? [],
    [teammatesData],
  );

  const options = useMemo<readonly ManagerOption[]>(() => {
    return teammates.map((t) => ({
      id: t.id,
      name: buildTeammateDisplayNames(t).primaryDisplayName,
    }));
  }, [teammates]);

  const isLoading = isFetching && !isFetchingNextPage;

  const css = `
/* Remove the Chrome default clear button */
[type="search"]::-webkit-search-cancel-button,
[type="search"]::-webkit-search-decoration {
  -webkit-appearance: none;
}

/* Work around for Tailwind + React Aria focus selector being broken on Autocomplete. */
[type="search"]:focus {
  --tw-ring-inset: inset;
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
}
`;

  return (
    <Select
      aria-label="Manager"
      isDisabled={props.isDisabled}
      value={props.managerId ?? NONE_OPTION_ID}
      onChange={(key) => {
        const id = key ? String(key) : null;
        props.onChange(id && id !== NONE_OPTION_ID ? asTeammateId(id) : null);
      }}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);

        if (!newIsOpen) {
          setSearch("");
        }
      }}
    >
      <Button className={selectStyle().button({ className: "min-w-[200px]" })}>
        {managerDisplayName ? (
          <SelectValue className={selectStyle().value()}>
            {managerDisplayName}
          </SelectValue>
        ) : (
          <SelectValue className={selectStyle().placeholder()}>
            None
          </SelectValue>
        )}
        <IconSelector className={selectStyle().chevron()} />
      </Button>

      <Popover
        className={menuStyle().popover({
          className: "flex h-[400px] w-[300px] flex-col",
        })}
      >
        {/* eslint-disable-next-line react/no-unknown-property */}
        <style scoped>{css}</style>

        <Autocomplete>
          <SearchField aria-label="Search managers" className="p-1">
            <Input
              className={inputStyle().input({
                className: "",
              })}
              placeholder="Search..."
              autoFocus
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchField>

          <ListBox
            aria-label="Manager options"
            className="flex-1 overflow-y-auto p-1"
            renderEmptyState={() =>
              isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader style={LoaderStyle.SPINNER} />
                </div>
              ) : (
                <div className="py-2 pr-4 pl-4 text-sm text-muted-foreground">
                  No matches found
                </div>
              )
            }
          >
            {props.managerId ? (
              <ListBoxItem
                key={props.managerId}
                id={props.managerId}
                textValue={managerDisplayName}
                className={menuStyle().item()}
              >
                {managerDisplayName}
              </ListBoxItem>
            ) : null}

            <ListBoxItem
              key={NONE_OPTION_ID}
              id={NONE_OPTION_ID}
              textValue="None"
              className={menuStyle().item()}
            >
              None
            </ListBoxItem>

            <Collection items={options.filter((o) => o.id !== props.managerId)}>
              {(option) => (
                <ListBoxItem
                  textValue={option.name}
                  className={menuStyle().item()}
                >
                  {option.name}
                </ListBoxItem>
              )}
            </Collection>

            <ListBoxLoadMoreItem
              onLoadMore={hasNextPage ? fetchNextPage : undefined}
              isLoading={isFetchingNextPage}
              className="flex items-center justify-center py-2"
            >
              <Loader style={LoaderStyle.SPINNER} />
            </ListBoxLoadMoreItem>
          </ListBox>
        </Autocomplete>
      </Popover>
    </Select>
  );
};
